/**
 * Shared admin-API helpers.
 *
 * The admin session is carried by an httpOnly `zog_admin` cookie set by
 * /api/admin/login. A second JS-readable cookie `zog_csrf` carries the CSRF
 * token. State-changing admin requests must echo the CSRF token in the
 * `X-CSRF-Token` header (double-submit pattern).
 *
 * Use `withAdminAuth(cfg)` for axios calls — it injects `withCredentials`
 * AND the CSRF header automatically.
 */

const CSRF_COOKIE_NAME = 'zog_csrf';

const readCsrfToken = () => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]+)`));
    return m ? decodeURIComponent(m[1]) : '';
  } catch {
    return '';
  }
};

// fetch() helper — always sends the admin cookie + CSRF header
export const adminFetch = (url, init = {}) => {
  const headers = new Headers(init.headers || {});
  const token = readCsrfToken();
  if (token) headers.set('X-CSRF-Token', token);
  return fetch(url, { ...init, credentials: 'include', headers });
};

// fetch-and-parse helper — throws on non-2xx with { detail } payload
export const adminFetchJson = async (url, init = {}) => {
  const res = await adminFetch(url, init);
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const err = new Error((data && data.detail) || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

// axios config helper — merge into any axios call
export const withAdminAuth = (cfg = {}) => {
  const token = readCsrfToken();
  return {
    ...cfg,
    withCredentials: true,
    headers: {
      ...(cfg.headers || {}),
      ...(token ? { 'X-CSRF-Token': token } : {}),
    },
  };
};

// Read-only convenience for places that just need the token
export const getCsrfToken = readCsrfToken;
